import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PlusIcon, 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  DocumentCheckIcon 
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const CreateClient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [formData, setFormData] = useState({
    whatsapp_number: '', // Phone number input
    agent_id: '' // Agent selection
  });

  // Format agents for SearchableSelect
  const formatAgentsForSelect = (agentsData) => {
    return agentsData.map(agent => ({
      value: agent.id,
      label: agent.name,
      description: agent.description || 'AI Assistant',
      icon: '🤖' // Default robot icon for agents
    }));
  };

  // Handle agent selection change
  const handleAgentChange = (e) => {
    setFormData(prev => ({
      ...prev,
      agent_id: e.target.value
    }));
  };

  // Fetch agents from database
  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await api.getAgents();
      const activeAgents = response.data.agents.filter(agent => agent.status === 'active');
      setAgents(activeAgents);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  // Check verification status and load agents on component mount
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const response = await api.getVerificationStatus();
        setVerificationStatus(response.data.verification_status);
      } catch (error) {
        console.error('Error checking verification status:', error);
        // Assume unverified if error
        setVerificationStatus('unverified');
      } finally {
        setCheckingVerification(false);
      }
    };

    checkVerificationStatus();
    fetchAgents();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check verification status before proceeding
    if (verificationStatus !== 'verified') {
      setShowVerificationPrompt(true);
      return;
    }

    setLoading(true);

    try {
      // Validate required fields first
      if (!formData.agent_id) {
        toast.error('Please select an agent');
        setLoading(false);
        return;
      }
      if (!formData.whatsapp_number) {
        toast.error('WhatsApp number is required');
        setLoading(false);
        return;
      }

      // Process WhatsApp number to WhatsApp ID format
      const phoneNumber = formData.whatsapp_number.replace(/\D/g, ''); // Remove non-digits
      let whatsapp_id;
      if (phoneNumber.length >= 9) {
        const last9Digits = phoneNumber.slice(-9);
        whatsapp_id = `233${last9Digits}@c.us`;
      } else {
        whatsapp_id = `233${phoneNumber}@c.us`;
      }

      // Prepare data for backend (backend will auto-generate other IDs)
      const submitData = {
        whatsapp_id: whatsapp_id,
        agent_id: formData.agent_id
      };

      // Show optimistic success message
      toast.success('Creating your WhatsApp agent...');
      
      const response = await api.createClient(submitData);
      
      // Always show success and redirect, even if there are connection issues
      // The agent is created successfully, WhatsApp connection can be handled on the details page
      toast.success(`WhatsApp agent ${response.data.client_id} created successfully!`);
      
      // Small delay to let user see the success message
      setTimeout(() => {
        navigate(`/dashboard/clients/${response.data.client_id}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error creating client:', error);
      
      // Check if the error is specifically about agent creation failure
      // vs WhatsApp connection issues
      const errorMessage = error.response?.data?.error || 'Failed to create agent';
      const isClientCreationError = errorMessage.toLowerCase().includes('already exists') ||
                                   errorMessage.toLowerCase().includes('validation') ||
                                   errorMessage.toLowerCase().includes('required') ||
                                   errorMessage.toLowerCase().includes('invalid');
      
      if (isClientCreationError) {
        // Show error for actual agent creation failures
        toast.error(errorMessage);
      } else {
        // For connection issues, still redirect but with a different message
        toast.success('WhatsApp agent created! Setting up connection...');
        
        // Extract client_id from error response if available
        const clientId = error.response?.data?.client_id || 
                        error.response?.data?.data?.client_id;
        
        if (clientId) {
          setTimeout(() => {
            navigate(`/dashboard/clients/${clientId}`);
          }, 1500);
        } else {
          // Fallback - go to clients list
          setTimeout(() => {
            navigate('/dashboard/clients');
          }, 1500);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Create New Agent</h1>
        <p className="text-gray-500 mt-1">Set up a new WhatsApp AI agent</p>
      </div>

      {/* Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Account Verification Required</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  To create WhatsApp agents, you need to verify your account with valid identification documents. 
                  This ensures security and compliance with WhatsApp Business API requirements.
                </p>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start">
                    <DocumentCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium">What you'll need:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>Valid Ghana ID (National ID, Driver's License, SSNIT, or Ghana Card)</li>
                        <li>Clear photos of your documents</li>
                        <li>Basic business information</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="items-center px-4 py-3">
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => navigate('/dashboard/verification')}
                    className="px-4 py-2 bg-whatsapp-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-whatsapp-600 focus:outline-none focus:ring-2 focus:ring-whatsapp-300"
                  >
                    Start Verification Process
                  </button>
                  <button
                    onClick={() => setShowVerificationPrompt(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Status Banner */}
      {!checkingVerification && verificationStatus !== 'verified' && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Account Verification Pending</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your account needs to be verified before you can create WhatsApp agents. 
                <button 
                  onClick={() => navigate('/dashboard/verification')}
                  className="ml-1 font-medium underline hover:text-yellow-900"
                >
                  Complete verification now
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {checkingVerification && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <ArrowPathIcon className="h-5 w-5 text-blue-600 animate-spin mr-3" />
            <p className="text-sm text-blue-700">Checking account verification status...</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Hidden fields - auto-generated on backend */}
          {/* Auto-generate toggle - hidden */}
          {/* Client ID - hidden, auto-generated */}
          {/* User ID - hidden, auto-generated on backend */}
          {/* Email - commented out, not mandatory */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WhatsApp Number */}
            <div>
              <label htmlFor="whatsapp_number" className="block text-sm font-medium text-gray-700">
                WhatsApp Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="whatsapp_number"
                id="whatsapp_number"
                required
                value={formData.whatsapp_number}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-whatsapp-500 focus:border-whatsapp-500 sm:text-sm"
                placeholder="233554694531"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter phone number (starting with 233)
              </p>
            </div>

            {/* Agent */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Agent <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="agent_id"
                options={formatAgentsForSelect(agents)}
                value={formData.agent_id}
                onChange={handleAgentChange}
                placeholder={loadingAgents 
                  ? 'Loading agents...' 
                  : agents.length === 0 
                  ? 'No agents available' 
                  : 'Search and select an agent...'
                }
                isRequired={true}
                isDisabled={loadingAgents || agents.length === 0}
                isSearchable={true}
                isClearable={true}
                className="mt-1"
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '38px',
                    height: '38px',
                    borderColor: state.isFocused ? '#25d366' : '#d1d5db',
                    borderWidth: '1px',
                    borderRadius: '0.5rem',
                    boxShadow: state.isFocused 
                      ? '0 0 0 2px rgba(37, 211, 102, 0.2)'
                      : provided.boxShadow,
                    '&:hover': {
                      borderColor: state.isFocused ? '#25d366' : '#9ca3af'
                    },
                    fontSize: '0.875rem',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '0 8px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center'
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: '0',
                    paddingTop: '0',
                    paddingBottom: '0'
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: '36px'
                  })
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                Select an AI agent for this client
              </p>
              {!loadingAgents && agents.length === 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  No active agents found.{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/agents/create')}
                    className="text-whatsapp-600 hover:text-whatsapp-700 font-medium"
                  >
                    Create an agent first
                  </button>
                </p>
              )}
            </div>
          </div>

          {/* Webhook URL */}
          <div>
            <label htmlFor="webhook_url" className="block text-sm font-medium text-gray-700">
              Webhook URL
            </label>
            <input
              type="url"
              name="webhook_url"
              id="webhook_url"
              value={formData.whatsapp_number ? `https://whatsapp-callback.snwolley.ai/bot/client_${formData.whatsapp_number.replace(/\D/g, '').slice(-9)}` : ''}
              disabled
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
              placeholder="Auto-generated webhook URL"
            />
            <p className="mt-1 text-xs text-gray-500">
              Auto-generated webhook URL for this client
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>  
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Agent
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          {/* <li>• Client ID and User ID will be auto-generated</li> */}
          {/* <li>• A new Docker container will be created for your agent</li> */}
          <li>• WhatsApp process will be automatically configured for you</li>
          <li>• WhatsApp session will be ready for QR code scanning</li>
          <li>• Webhook will be configured with auto-generated API key</li>
          <li>• Selected agent will be assigned to handle messages</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateClient;
